// Extracts the `error` field from a failed response's `{ error: "..." }` JSON body, falling back to the
// raw body (or the response's status text, if the body is empty) when it isn't that shape. Without this,
// structured error bodies surfaced to users verbatim — e.g. a rename clash read as
// `Rename failed (409): {"error":"\"0050.json\" already exists in this environment."}` — instead of the
// plain sentence the backend wrote for exactly this purpose.
function extractErrorMessage(body, fallback) {
    if (!body) {
        return fallback;
    }
    try {
        const parsed = JSON.parse(body);
        if (parsed.error) {
            return parsed.error;
        }
    }
    catch {
        // Not JSON — show the raw body as-is.
    }
    return body;
}
// Generates a level on the backend from the given size + options and returns the level data.
export async function generateLevel(request) {
    const response = await fetch("/api/content/generate-level", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Generate failed (${response.status}): ${body || response.statusText}`);
    }
    return (await response.json());
}
// Compiles a template AND weighs its colour books in ONE round trip. Both halves must come from the same
// compile — asking two endpoints would compile twice and could report on a level the editor is not showing.
export async function compileLevelWithReport(template) {
    const response = await fetch("/api/content/compile-level/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(template),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Compile failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// What each channel is serving, read off the sites themselves.
export async function fetchDeployments() {
    const response = await fetch("/api/content/deployments");
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Reading deployments failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// The level ids inside a channel's live pack. Separate call because the pack is the big file.
export async function fetchDeployedLevels(channel) {
    const response = await fetch(`/api/content/deployments/${encodeURIComponent(channel)}/levels`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Reading the ${channel} pack failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// The live pack — a zip of one JSON per level — for saving to disk. Fetched through the backend
// rather than straight from the public site so it behaves the same as the level-id listing beside it
// and does not depend on the CORS header a particular deploy happened to carry.
export async function fetchDeployedPack(channel) {
    const response = await fetch(`/api/content/deployments/${encodeURIComponent(channel)}/pack`);
    if (!response.ok) {
        // Read the error as TEXT: a failure is our JSON error body, not the archive.
        const body = await response.text();
        throw new Error(extractErrorMessage(body, response.statusText));
    }
    // The server names the file (site + pack version); fall back to something honest if the header is
    // ever stripped by a proxy.
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    return { blob: await response.blob(), fileName: match?.[1] ?? `${channel}-pack.zip` };
}
export async function fetchDeployStatus() {
    const response = await fetch("/api/content/deploy-status");
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Deploy status failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// Publishes an environment's COMPILED levels to a channel. The backend refuses an environment with
// none — a pack replaces the previous one wholesale on the client, so an empty deploy takes away
// everything that channel serves rather than doing nothing.
export async function deployLevels(from, channel) {
    const response = await fetch("/api/content/deploy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from, channel }),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, response.statusText));
    }
    return (await response.json());
}
// Creates a deploy channel — a fresh Hosting site of its own, so deploying to it can never touch
// another channel. The name is slugified server-side; what comes back is the name that stuck.
export async function createChannel(name) {
    const response = await fetch("/api/content/channels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, response.statusText));
    }
    return (await response.json());
}
// Deletes a custom channel AND its Hosting site (the main channel is refused). Anything still fetching
// from it starts seeing 404s, which the game's pack sync treats as "keep what you have". With keepSite
// it only takes the channel off this list and the site keeps serving — the only way an ADDED channel
// goes, since this editor never deletes a site it did not create.
export async function deleteChannel(channel, keepSite = false) {
    const query = keepSite ? "?keepSite=true" : "";
    const response = await fetch(`/api/content/channels/${encodeURIComponent(channel)}${query}`, { method: "DELETE" });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, response.statusText));
    }
}
export async function fetchAvailableSites() {
    const response = await fetch("/api/content/channels/available");
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, response.statusText));
    }
    return (await response.json()).sites;
}
// Marks whether this editor owns a channel's site — i.e. whether it may delete it. Database only.
export async function setChannelOwnership(channel, ownsSite) {
    const response = await fetch(`/api/content/channels/${encodeURIComponent(channel)}/ownership`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownsSite }),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, response.statusText));
    }
}
// Lists an EXISTING site as a channel under `name`. Nothing is created in Firebase.
export async function addChannel(siteId, name) {
    const response = await fetch("/api/content/channels/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ siteId, name }),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, response.statusText));
    }
    return (await response.json());
}
// Lists a target's PUBLISHED levels — what the game would play (no target = production).
export async function listGameLevels(target) {
    const query = target ? `?target=${encodeURIComponent(target)}` : "";
    const response = await fetch(`/api/content/game-levels${query}`);
    if (!response.ok) {
        // The reads surface the backend's message too. A stale tab holding an invalid target gets a 400
        // saying exactly what is wrong with it; without this the banner showed only the status code and
        // threw the one actionable sentence away.
        const body = await response.text();
        throw new Error(`Listing game levels failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// Downloads a compiled level's raw JSON. Returned as text so it can be sent straight to Unity to play.
export async function getGameLevelContent(id) {
    const response = await fetch(`/api/content/game-level/${encodeURIComponent(id)}`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Downloading level failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return await response.text();
}
// Lists a target's authored templates (no target = production).
export async function listEditorLevels(target) {
    const query = target ? `?target=${encodeURIComponent(target)}` : "";
    const response = await fetch(`/api/content/editor-levels${query}`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Listing editor levels failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// Downloads a template's raw JSON content.
export async function getEditorLevelContent(id) {
    const response = await fetch(`/api/content/editor-level/${encodeURIComponent(id)}`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Downloading template failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return await response.text();
}
// Saves (creates/overwrites) a template in the target.
export async function saveEditorLevel(template, target) {
    const response = await fetch(`/api/content/editor-level?target=${encodeURIComponent(target)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(template),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Save failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// Deletes a level — template and compiled half together, in one transaction. The target is REQUIRED:
// the backend verifies the level actually belongs to it before deleting (see
// ContentController.DeleteEditorLevel).
//
// ⚠ This is PERMANENT. Over Drive a delete moved the files to a trash they could be recovered from;
// a row delete has no second chance.
export async function deleteEditorLevel(id, target) {
    const response = await fetch(`/api/content/editor-level/${encodeURIComponent(id)}?target=${encodeURIComponent(target)}`, { method: "DELETE" });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Delete failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
}
// Renames a level inside the target — stored name and the template's own Name field together.
export async function renameEditorLevel(id, name, target) {
    const response = await fetch(`/api/content/editor-level/${encodeURIComponent(id)}/rename?target=${encodeURIComponent(target)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Rename failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// Compiles + publishes a template: stores the compiled level the game plays, and keeps the template
// re-editable. Both halves are one row, so they cannot disagree.
export async function publishLevel(template, target) {
    const response = await fetch(`/api/content/publish-level?target=${encodeURIComponent(target)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(template),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Publish failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// Lists production plus every dev environment.
export async function listTargets() {
    const response = await fetch("/api/content/targets");
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Listing targets failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
// Creates a dev environment, optionally as a COPY of another environment — every level, both halves
// (template and compiled), byte for byte, the way promote copies. `copyFrom` is an environment id
// ("production", "dev/x") or null to start empty. Throws with the server's message on a duplicate
// name (409), an invalid one, or an unknown source (400) so the form can show it verbatim.
//
// templatesFailed/seedError are always 0/null now: the environment and its copied levels are one
// transaction, so a copy cannot partially fail. They stay in the type because the server still sends
// them and the UI still reads them.
export async function createDevTarget(name, copyFrom) {
    const response = await fetch("/api/content/targets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, copyFrom }),
    });
    const body = await response.text();
    if (!response.ok) {
        throw new Error(extractErrorMessage(body, response.statusText));
    }
    const created = JSON.parse(body);
    return {
        id: created.id,
        name: created.name,
        isProduction: false,
        templatesCopied: created.templatesCopied,
        compiledCopied: created.compiledCopied ?? 0,
        templatesFailed: created.templatesFailed,
        seedError: created.seedError ?? null,
    };
}
// Deletes a dev environment and every level in it. The backend rejects "production" regardless of what
// the caller sends, so this can never be used to delete production.
//
// ⚠ PERMANENT — see deleteEditorLevel.
export async function deleteDevTarget(id) {
    const response = await fetch(`/api/content/targets?target=${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Delete failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
}
// Copies the named levels from a dev environment into production, byte for byte.
//
// `overwriteExisting` is the server's guard, not a UI convenience: without it the backend skips every
// name that already exists in production rather than writing over it. Pass true only for a promote the
// user confirmed KNOWING it overwrites — when the confirmation promised that nothing would be replaced,
// leaving it false makes the server keep that promise even if production changed in the meantime.
export async function promoteLevels(from, names, overwriteExisting) {
    const response = await fetch("/api/content/promote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from, names, overwriteExisting }),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new Error(`Promote failed (${response.status}): ${extractErrorMessage(body, response.statusText)}`);
    }
    return (await response.json());
}
export async function fetchHarborVersions(target) {
    const response = await fetch(`/api/harbor/versions?target=${encodeURIComponent(target)}`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, `Reading Harbor versions failed (${response.status})`));
    }
    return (await response.json());
}
export async function requestHarborDeleteChallenge(target) {
    const response = await fetch(`/api/harbor/delete-challenge?target=${encodeURIComponent(target)}`, { method: "POST" });
    const body = await response.text();
    if (!response.ok) {
        throw new Error(extractErrorMessage(body, `Could not get a confirmation code (${response.status})`));
    }
    return JSON.parse(body);
}
// Deletes the Harbor environment FIRST, then the dev environment here with every level in it — so a
// refused Harbor delete (wrong code, expired) leaves both untouched.
export async function deleteEnvironmentWithHarbor(target, confirmKey, challengeId, code) {
    const response = await fetch("/api/harbor/delete-environment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target, confirmKey, challengeId, code }),
    });
    const body = await response.text();
    if (!response.ok) {
        throw new Error(extractErrorMessage(body, `Delete failed (${response.status})`));
    }
    return JSON.parse(body);
}
// Publishes an environment's compiled levels to Harbor, calling onEvent for every step. Resolves with
// the last event (done / unchanged / error); throws only when the request itself fails.
export async function publishToHarbor(target, comment, onEvent) {
    const response = await fetch("/api/harbor/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target, comment }),
    });
    if (!response.ok || !response.body) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, `Publishing failed (${response.status})`));
    }
    // The body is JSON lines, flushed one per step.
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let last = null;
    for (;;) {
        const { value, done } = await reader.read();
        if (value) {
            buffer += decoder.decode(value, { stream: true });
        }
        let newline;
        while ((newline = buffer.indexOf("\n")) >= 0) {
            const line = buffer.slice(0, newline).trim();
            buffer = buffer.slice(newline + 1);
            if (line) {
                last = JSON.parse(line);
                onEvent(last);
            }
        }
        if (done) {
            break;
        }
    }
    if (last === null || (last.phase !== "done" && last.phase !== "unchanged" && last.phase !== "error")) {
        return { phase: "error", error: "The connection closed before the publish finished. Harbor keeps nothing from an unfinished publish." };
    }
    return last;
}
// --- Level resources ---------------------------------------------------------------------------------
// Files made from each compiled level by the embedded player — its screenshot and its walls — stored by
// Controllers/LevelResourcesController.cs per level and kind, tied to the content hash of the JSON they
// show (the same hash Harbor keys its resources by). See levelResources.ts.
/** Every kind this editor makes for a level, in display order (mirrors LevelResource.Kinds). */
export const LEVEL_RESOURCE_KINDS = ["screenshot", "walls"];
export async function fetchLevelResourceStatus(target) {
    const response = await fetch(`/api/level-resources/status?target=${encodeURIComponent(target)}`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, `Reading level resources failed (${response.status})`));
    }
    return (await response.json());
}
/** A stored resource (or its thumbnail); the hash makes a new one a new URL. */
export function levelResourceUrl(levelId, kind, hash, size = "full") {
    return `/api/level-resources/${encodeURIComponent(levelId)}/${kind}?h=${encodeURIComponent(hash.slice(0, 16))}${size === "thumb" ? "&size=thumb" : ""}`;
}
/** Stores a level's resource, or (thumb) the small copy of the one stored for that hash. */
export async function putLevelResource(levelId, kind, jsonHash, png, thumb = false) {
    const body = png instanceof Blob ? png : new Blob([png.slice().buffer], { type: "image/png" });
    const response = await fetch(`/api/level-resources/${encodeURIComponent(levelId)}/${kind}?jsonHash=${encodeURIComponent(jsonHash)}${thumb ? "&thumb=true" : ""}`, { method: "PUT", headers: { "Content-Type": "image/png" }, body });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(extractErrorMessage(text, `Saving the ${kind} failed (${response.status})`));
    }
}
/** For each compiled level of an environment, the resource kinds Harbor already has (same name + content). */
export async function fetchHarborResourceAvailability(target) {
    const response = await fetch(`/api/harbor/resources/available?target=${encodeURIComponent(target)}`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, `Asking Harbor for level resources failed (${response.status})`));
    }
    return (await response.json());
}
/** Harbor's resource of this kind for a level as it is now. */
export async function fetchHarborResource(levelId, kind) {
    const response = await fetch(`/api/harbor/resources/${encodeURIComponent(levelId)}/${kind}`);
    if (!response.ok) {
        const body = await response.text();
        throw new Error(extractErrorMessage(body, `Downloading Harbor's ${kind} failed (${response.status})`));
    }
    return new Uint8Array(await response.arrayBuffer());
}
